import { useMemo } from 'react'
import styled, { css } from 'styled-components'

const DAYS_BEFORE = 2
const DAYS_AFTER  = 5

const NavWrapper = styled.div`
    flex-shrink: 0;
    background: ${({ theme }) => theme.card.background};
    border-bottom: 1px solid ${({ theme }) => theme.area.lightBorder};
    padding: 0.4rem 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    overflow: hidden;

    @media (max-width: 768px) {
        display: none;
    }
`

const ScrollRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 0;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
    &::-webkit-scrollbar { display: none; }
`

const DayColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex-shrink: 0;
    padding: 0 2px;
`

const ShiftsRow = styled.div`
    display: flex;
    flex-direction: row;
    gap: 2px;
`

const baseBtn = css`
    background: none;
    border: 1px solid ${({ theme, $active }) => $active ? theme.btn.primary : theme.area.lightBorder};
    border-radius: 0.3rem;
    cursor: pointer;
    font-size: var(--font-XS);
    text-align: center;
    padding: 2px 4px;
    white-space: nowrap;
    transition: background 0.12s, border-color 0.12s, color 0.12s;
    line-height: 1.4;
`

const DayBtn = styled.button`
    ${baseBtn}
    font-weight: 600;
    color: ${({ theme, $active }) => $active ? theme.btn.primary : theme.font.color};
    background: ${({ theme, $active }) => $active ? `${theme.btn.primary}14` : 'transparent'};
    width: 100%;

    &:hover {
        background: ${({ theme, $active }) => $active ? `${theme.btn.primary}22` : theme.area.highBg};
        border-color: ${({ theme }) => theme.btn.primary};
    }
`

const ShiftBtn = styled.button`
    ${baseBtn}
    color: ${({ theme, $active }) => $active ? theme.card.background : theme.font.label};
    background: ${({ theme, $active }) => $active ? theme.btn.primary : 'transparent'};
    border-color: ${({ theme, $active }) => $active ? theme.btn.primary : theme.area.lightBorder};
    flex: 1;

    &:hover {
        background: ${({ theme, $active }) => $active ? theme.btn.primary : theme.area.highBg};
        border-color: ${({ theme }) => theme.btn.primary};
        color: ${({ theme, $active }) => $active ? theme.card.background : theme.font.color};
    }
`

const fmtDay = (d) =>
    d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })

const fmtHour = (h) => `${String(h).padStart(2, '0')}:00`

const WorkPlannerQuickNav = ({ shiftStart, shiftHours, defaultShiftStartHour, onGoToShift }) => {
    const shiftsPerDay = Math.round(24 / shiftHours)
    // Track dayOffset separately so a shift that wraps past midnight correctly
    // lands on the next calendar day (e.g. 08/16/00 pattern: the "00" shift
    // belongs to the following day, not the column day).
    const shiftSlots = Array.from({ length: shiftsPerDay }, (_, i) => {
        const totalHours = defaultShiftStartHour + i * shiftHours
        return { hour: totalHours % 24, dayOffset: Math.floor(totalHours / 24) }
    })

    const days = useMemo(() => {
        const base = new Date(shiftStart)
        base.setHours(0, 0, 0, 0)
        return Array.from({ length: DAYS_BEFORE + 1 + DAYS_AFTER }, (_, i) => {
            const d = new Date(base)
            d.setDate(d.getDate() + (i - DAYS_BEFORE))
            return d
        })
    }, [shiftStart])

    const shiftStartMs = shiftStart.getTime()

    const getShiftTime = (day, hour, dayOffset = 0) => {
        const d = new Date(day)
        if (dayOffset) d.setDate(d.getDate() + dayOffset)
        d.setHours(hour, 0, 0, 0)
        return d
    }

    const isActiveDay = (day) => {
        const dayOf = new Date(day); dayOf.setHours(0, 0, 0, 0)
        const curOf = new Date(shiftStart); curOf.setHours(0, 0, 0, 0)
        return dayOf.getTime() === curOf.getTime()
    }

    const isActiveShift = (day, { hour, dayOffset }) =>
        getShiftTime(day, hour, dayOffset).getTime() === shiftStartMs

    return (
        <NavWrapper>
            <ScrollRow>
                {days.map((day, i) => (
                    <DayColumn key={i}>
                        <DayBtn
                            $active={isActiveDay(day)}
                            onClick={() => onGoToShift(getShiftTime(day, defaultShiftStartHour))}
                            title={fmtDay(day)}
                        >
                            {fmtDay(day)}
                        </DayBtn>
                        <ShiftsRow>
                            {shiftSlots.map(({ hour, dayOffset }) => (
                                <ShiftBtn
                                    key={hour}
                                    $active={isActiveShift(day, { hour, dayOffset })}
                                    onClick={() => onGoToShift(getShiftTime(day, hour, dayOffset))}
                                >
                                    {fmtHour(hour)}
                                </ShiftBtn>
                            ))}
                        </ShiftsRow>
                    </DayColumn>
                ))}
            </ScrollRow>
        </NavWrapper>
    )
}

export default WorkPlannerQuickNav
